import { Container } from '@common/components/container';
import FiltersToolbar from '@common/components/filters-toolbar/filters-toolbar';
import { EPredicateFilters } from '@common/components/predicates-filter';
import { EPredicateFields, EPredicateOperators } from '@common/definitions';
import { useAppSelector, useDidComponentMount } from '@common/hooks';
import React, { useEffect, useMemo, useState } from 'react';
import * as organisationSelectors from '../../../organisation/selectors/organisation';
import { useTranslation } from 'react-i18next';
import DataTable from '@common/components/datatable';
import ProfileImage from '@common/components/profile-image';
import type {
  DataTableColumn,
  DataTableRow,
  SortConfig,
} from '@common/components/datatable/types';
import Overview from '@common/components/overview';
import Placeholder from '@common/components/placeholder';
import { Button } from '@common/components/button';
import EmployeeColumnModal from '@modules/performance/components/employee-column-modal/employee-column-modal';
import { useRequest } from '@common/services/api';
import {
  Category,
  Employee,
  EmployeeScore,
  PerformanceReportData,
  PerformanceReportMeta,
} from '@modules/performance/types';
import { omit } from 'lodash';

const PerformanceEmployeeContainer = () => {
  const { t } = useTranslation();
  const networks = useAppSelector(organisationSelectors.networks);
  const functions = useAppSelector(organisationSelectors.functions);
  const organisation = useAppSelector(organisationSelectors.selected);
  const [pageSize, setPageSize] = useState(20);
  const [sortConfig, setSortConfig] = useState<SortConfig | undefined>();
  const didMount = useDidComponentMount();

  const { data: categoryData } = useRequest(
    `/v1/organisations/${organisation.id}/performance/competency/categories`
  );

  const categories: Category[] = categoryData || [];

  const [predicates, setPredicates] = useState([
    {
      filter: EPredicateFilters[EPredicateFields.NETWORK],
      value: {
        attribute: EPredicateFields.NETWORK,
        comparison: EPredicateOperators.IN,
        value: [],
      },
      options: networks || [],
    },
    {
      filter: EPredicateFilters[EPredicateFields.FUNCTION],
      value: {
        attribute: EPredicateFields.FUNCTION,
        comparison: EPredicateOperators.IN,
        value: [],
      },
      options: functions || [],
    },
    {
      filter: EPredicateFilters[EPredicateFields.COMPETENCY_CATEGORY],
      value: {
        attribute: EPredicateFields.COMPETENCY_CATEGORY,
        comparison: EPredicateOperators.IN,
        value: [],
      },
      options: categories || [],
    },
  ]);

  useEffect(() => {
    if (categories.length > 0) {
      setPredicates((prev) => prev.map((predicate) => {
          if (
            predicate.filter ===
            EPredicateFilters[EPredicateFields.COMPETENCY_CATEGORY]
          ) {
            return {
              ...predicate,
              options: categories,
            };
          }
          return predicate;
        })
      );
    }
  }, [categories]);

  const [networkIds, functionIds, categoryIds] = useMemo(() => {
    return [
      predicates.find(
        (p) => p.filter === EPredicateFilters[EPredicateFields.NETWORK]
      )?.value.value,
      predicates.find(
        (p) => p.filter === EPredicateFilters[EPredicateFields.FUNCTION]
      )?.value.value,
      predicates.find(
        (p) => p.filter === EPredicateFilters[EPredicateFields.COMPETENCY_CATEGORY]
      )?.value.value,
    ];
  }, [predicates]);


  const { sortBy, competencyId } = useMemo(() => {
    if (!sortConfig) return { sortBy: undefined, competencyId: undefined };

    const { key, sortOrder } = sortConfig;

    if (key.startsWith('competency_')) {
      const extractedCompetencyId = key.replace('competency_', '');
      return {
        sortBy: `competency_score_${sortOrder}`,
        competencyId: extractedCompetencyId,
      };
    }

    if (key === 'employee') {
      return {
        sortBy: `employee_${sortOrder}`,
        competencyId: undefined,
      };
    }

    return {
      sortBy: `${key}_${sortOrder}`,
      competencyId: undefined,
    };
  }, [sortConfig]);

  const payload = useMemo(
    () => ({
      ...(networkIds ? { networkIds } : {}),
      ...(functionIds ? { functioIds: functionIds } : {}),
      ...(categoryIds ? { categoryIds } : {}),
      ...(sortBy ? { sortBy } : {}),
      ...(competencyId ? { competencyId } : {}),
      // version: 1,
    }),
    [networkIds, functionIds, categoryIds, sortBy, competencyId]
  );

  const apiConfig = useMemo(
    () => ({
      useCursor: true,
      limit: pageSize,
      payload,
      transform: (res: any) => {
        return {
          ...res,
        };
      },
    }),
    [payload, pageSize]
  );

  const {
    data: employeesDataRaw,
    loading: employeesLoading,
    refetch: employeesRefetch,
    reset: employeesReset,
    meta: employeesMeta,
  } = useRequest(
    `/v1/organisations/${organisation.id}/performance/reports/employees`,
    apiConfig
  );

  const employeesData: PerformanceReportData = employeesDataRaw;
  const meta: PerformanceReportMeta = employeesMeta;

  useEffect(() => {
    if (!didMount) return;
    employeesReset();
  }, [pageSize, sortConfig, didMount, employeesReset]);

  const employees: DataTableRow[] = React.useMemo(() => {
    if (!employeesData || !Array.isArray(employeesData)) return [];

    return employeesData.map((user: Employee) => {
      const row: DataTableRow = {
        id: user.id,
        name: `${user.first_name} ${user.last_name}`,
        avatar: user.profile_img,
        functions: user.functions,
        networks: user.networks,
        average_rating: user.average_rating,
        evaluation_count: user.evaluation_count,
        last_evaluation_at: user.last_evaluation_at,
      };

      user.scores.forEach((score: EmployeeScore) => {
        row[`competency_${score.competency_id}`] = score.score;
      });

      return row;
    });
  }, [employeesData]);

  const competencies = useMemo(() => {
    return meta?.competencies || [];
  }, [meta]);

  const ratingScales = useMemo(() => {
    return meta?.rating_scales || [];
  }, [meta]);

  // const getAverage = (row: DataTableRow) => {
  //   const competencyKeys = competencies.map((c: any) => `competency_${c.id}`);
  //   const values = competencyKeys
  //     .map((key: string) => row[key])
  //     .filter((v: any) => v !== undefined && v !== null);

  //   if (values.length === 0) return '0.0';
  //   const sum = values.reduce((s: number, v: number) => s + v, 0);
  //   return (sum / values.length).toFixed(1);
  // };

  const getColorFromRatingScale = (score: number): string => {
    const sortedScales = [...ratingScales].sort((a, b) => a.index - b.index);

    const roundedScore = Math.round(score);
    const matchingScale = sortedScales.find(
      (scale) => scale.index === roundedScore
    );

    return matchingScale?.color || '#9ca3af';
  };

  const renderScore = (value: number) => {
    if (value === undefined || value === null) {
      return (
        <div className="tw-w-full tw-h-[50px] tw-flex tw-items-center tw-justify-center">
          <span className="tw-text-gray-400">-</span>
        </div>
      );
    }

    const bgColor = getColorFromRatingScale(value);

    return (
      <div
        className="tw-w-full tw-h-[50px] tw-flex tw-flex-row tw-rounded-sm tw-items-center tw-justify-center"
        style={{ backgroundColor: bgColor }}
      >
        <div
          className="tw-w-full tw-h-full tw-rounded-sm tw-flex tw-flex-row tw-gap-[6px] tw-items-center tw-justify-center"
          style={{ backgroundColor: bgColor }}
        >
          <div className="tw-text-gray-custom-50 tw-text-base-plus tw-font-semibold">
            {value}
          </div>
          <div className="tw-text-gray-custom-50 tw-text-sm tw-font-medium">
            /
          </div>
          <div className="tw-text-gray-custom-50 tw-text-sm tw-font-medium">
            {ratingScales.length}
          </div>
        </div>
      </div>
    );
  };

  const columns: DataTableColumn[] = React.useMemo(() => {
    const baseColumns: DataTableColumn[] = [
      {
        key: 'employee',
        label: t('performance:employees'),
        options: {
          render: (row: DataTableRow) => (
            <div className="tw-flex tw-items-center tw-gap-3">
              <ProfileImage
                user={{ full_name: row.name, profile_img: row.avatar }}
                size={28}
              />
              <span className="tw-text-base tw-text-gray-900 tw-font-medium">
                {row.name}
              </span>
            </div>
          ),
        },
      },
    ];

    const competencyColumns: DataTableColumn[] = competencies.map(
      (competency: any) => ({
        key: `competency_${competency.id}`,
        label: competency.name.toUpperCase(),
        className: '!tw-p-0 !tw-px-[0.5px]',
        options: {
          align: 'center' as const,
          size: 175,
          sortable: true,
          renderLite: (v: number) => renderScore(v),
        },
      })
    );

    return [...baseColumns, ...competencyColumns];
  }, [competencies, ratingScales, t]);

  const [visibleKeys, setVisibleKeys] = useState<string[]>([]);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);

  useEffect(() => {
    const allKeys = columns.map((c) => c.key);
    setVisibleKeys(allKeys);
    setColumnOrder(allKeys);
  }, [columns]);

  const handleColumnChange = (orderedKeys: string[]) => {
    setVisibleKeys(orderedKeys);
    const allKeys = columns.map((c) => c.key);
    const orderedAllKeys = [
      ...orderedKeys,
      ...allKeys.filter((k) => !orderedKeys.includes(k)),
    ];
    setColumnOrder(orderedAllKeys);
  };

  const orderedColumns = useMemo(() => {
    if (columnOrder.length === 0) return columns;

    const columnMap = new Map(columns.map((col) => [col.key, col]));
    const ordered = columnOrder
      .map((key) => columnMap.get(key))
      .filter((col): col is DataTableColumn => col !== undefined);

    columns.forEach((col) => {
      if (!columnOrder.includes(col.key)) {
        ordered.push(col);
      }
    });

    return ordered;
  }, [columns, columnOrder]);

  const onSort = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) {
      setSortConfig({ key, sortOrder: 'asc' });
      return;
    }
    setSortConfig({
      key,
      sortOrder: sortConfig.sortOrder === 'asc' ? 'desc' : 'asc',
    });
  };

  // Data is now sorted by the API, so we don't need client-side sorting
  // const sortedData = employees;

  // const handleExportPDF = () => {
  //   if (!employeesData || !employees.length) {
  //     return;
  //   }

  //   exportEmployeePerformancePDF({
  //     employees: sortedData,
  //     competencies: competencies,
  //     ratingScales: ratingScales,
  //     organisationName: organisation.name,
  //   });
  // };

  return (
    <Container>
      <Container.Content>
        <div className="tw-flex tw-flex-row tw-justify-between tw-items-center tw-mb-4">
          <FiltersToolbar
            searchEnabled={false}
            predicates={predicates}
            setPredicates={setPredicates}
            searchPlaceholder={t('common:search_list_search_placeholder')}
          />
          <div className="tw-flex tw-flex-row tw-justify-end tw-gap-2">
            {/* <Button
              icon="download"
              type="white"
              size="large"
              // onClick={handleExportPDF}
              disabled={!employees.length}
            >
              {t('performance:export')}
            </Button> */}
            <EmployeeColumnModal
              columns={orderedColumns}
              visibleKeys={visibleKeys}
              onChange={handleColumnChange}
            >
              <Button icon="grid_table" type="white" size="large">
                {t('performance:columns')}
              </Button>
            </EmployeeColumnModal>
          </div>
        </div>

        <Overview>
          <DataTable
            columns={orderedColumns}
            visibleColumnKeys={visibleKeys}
            data={employees.map((e) => ({ ...e, employee: e.name }))}
            stickyHeader
            stickyFirstColumn
            sortConfig={sortConfig}
            onShowMore={employeesRefetch}
            pagination={{
              ...omit(apiConfig, 'payload'),
              ...meta?.pagination,
              mode: 'paginated',
            }}
            onPageChange={(newPageSize) => {
              setPageSize(newPageSize);
            }}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
            }}
            loading={employeesLoading}
            onSort={onSort}
            emptyView={
              <Placeholder title={t('performance:no_employees_placeholder')} />
            }
          />
        </Overview>
      </Container.Content>
    </Container>
  );
};

export default PerformanceEmployeeContainer;
